import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs/UniInput';
import { CountryPhoneDail, MobileNo } from '@/features/account-management/types';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { UpdateMobileNoStackParamList, UpdateMobileNoStackRoutes } from '@/features/account-management/screens/update-mobile-no/type';
import { phoneNumberProp, useCountriesGetPhoneDailInList } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { Card } from '@utx/utx-access-client';

export interface UpdateMobileNoProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: MobileNo) => void;
  func?: (cardId: string, phoneNumber: phoneNumberProp, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: Card;
}

export const UpdateMobileNoWebZeroScreen: React.FC<UpdateMobileNoProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const {
    body,
    textMb,
    textTitle,
    bgView,
    currentMobileView,
    textCurrentMobile,
    mobileView,
    mobileCodeView,
    inputSx,
    mobileNumberView,
    btnView,
    btn
  } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<UpdateMobileNoStackParamList>>();

  const [mobileNo, setMobileNo] = useState<MobileNo>({
    phoneCode: undefined,
    phoneNumber: undefined
  });

  const [errorMsg, setErrorMsg] = useState<MobileNo & { phoneCodeNumber: string | undefined }>({
    phoneCode: undefined,
    phoneNumber: undefined,
    phoneCodeNumber: undefined
  });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !mobileNo.phoneCode) || (keys.includes('phoneCode') && !data?.phoneCode)) {
      errorMsgFlag.phoneCode = ' ';
      errorMsgFlag.phoneCodeNumber = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && mobileNo.phoneCode) || (keys.includes('phoneCode') && data?.phoneCode)) {
      if (
        !countriesGetPhoneDailInList.find(
          (item: CountryPhoneDail) => item?.dailInCode == (!keysLength ? mobileNo.phoneCode : data?.phoneCode)
        )
      ) {
        errorMsgFlag.phoneCode = ' ';
        errorMsgFlag.phoneCodeNumber = t('card-account-management::glossary.invalid-country-code');
        validateFlag = false;
      } else {
        errorMsgFlag.phoneCode = undefined;
        errorMsgFlag.phoneCodeNumber = undefined;
      }
    }

    if ((!keysLength && !mobileNo.phoneNumber) || (keys.includes('phoneNumber') && !data?.phoneNumber)) {
      errorMsgFlag.phoneNumber = ' ';
      errorMsgFlag.phoneCodeNumber = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && mobileNo.phoneNumber) || (keys.includes('phoneNumber') && data?.phoneNumber)) {
      let phoneNumberFlag = (!keysLength ? mobileNo.phoneNumber : data?.phoneNumber) || '';
      if (phoneNumberFlag?.length < 4 || phoneNumberFlag?.length > 20) {
        errorMsgFlag.phoneNumber = ' ';
        errorMsgFlag.phoneCodeNumber = t('card-account-management::glossary.invalid-phone-number-length');
        validateFlag = false;
      } else {
        errorMsgFlag.phoneNumber = undefined;
        errorMsgFlag.phoneCodeNumber = undefined;
      }
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const updateMobileNo = async () => {
    const validate = await validateForm();
    if (validate) {
      onData(mobileNo);
      navigation.navigate(UpdateMobileNoStackRoutes.Confirm, {
        onLayout,
        data: mobileNo,
        func,
        onReturnData,
        refresh,
        onChangeView
      });
    }
  };

  const [showView, setShowView] = useState(false);
  const onChangeView = () => {
    setShowView(!showView);
  };

  const { countriesGetPhoneDailInList, isLoadingCountriesGetPhoneDailInList } = useCountriesGetPhoneDailInList();
  const [countryPhoneDail, setCountryPhoneDail] = useState<CountryPhoneDail | undefined>(undefined);
  useEffect(() => {
    if (!isLoadingCountriesGetPhoneDailInList) {
      let countryPhoneDailFlag = countriesGetPhoneDailInList.find(
        (cpd: CountryPhoneDail) => cpd?.dailInCode && record?.mobile?.indexOf(cpd?.dailInCode) == 0
      );
      setCountryPhoneDail(countryPhoneDailFlag);
    }
  }, [isLoadingCountriesGetPhoneDailInList]);
  const showPhoneCode = () => {
    return countryPhoneDail ? countryPhoneDail?.dailInCode : '-';
  };
  const showPhoneNumber = () => {
    let phoneNumberFlag = record?.mobile;
    if (countryPhoneDail) phoneNumberFlag = phoneNumberFlag?.replace(`${countryPhoneDail?.dailInCode}`, '');
    return phoneNumberFlag && phoneNumberFlag?.length > 4
      ? `${phoneNumberFlag?.substring(0, 2)}****${phoneNumberFlag?.substring(phoneNumberFlag?.length - 2)}`
      : phoneNumberFlag || '-';
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader title={t('card-account-management::replace-card.title--update-mobile-number')} closeModal={onClose} />
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::replace-card.text--enter-new-mobile-number')}
        </TextBody>
        <View sx={bgView}>
          <View sx={currentMobileView}>
            <TextCaption sx={textCurrentMobile}>{t('card-account-management::replace-card.text--current-mobile-number')}</TextCaption>
            <TextBody>{!isLoadingCountriesGetPhoneDailInList ? `(${showPhoneCode()}) ${showPhoneNumber()}` : record?.mobile}</TextBody>
          </View>
        </View>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::replace-card.text--new-mobile-number')}
        </TextCaption>
        <View sx={mobileView}>
          <View sx={mobileCodeView}>
            <UniInput
              size="medium"
              sx={inputSx}
              value={mobileNo?.phoneCode}
              errorText={errorMsg.phoneCode}
              regular={/[^\d-]/g}
              onValueChange={(value) => {
                setMobileNo({ ...mobileNo, phoneCode: value });
                validateForm({ phoneCode: value });
              }}
            />
          </View>
          <View sx={mobileNumberView}>
            <UniInput
              size="medium"
              sx={inputSx}
              value={mobileNo?.phoneNumber}
              errorText={errorMsg.phoneNumber}
              regular={UseInputRegularVariant.number}
              onValueChange={(value) => {
                setMobileNo({ ...mobileNo, phoneNumber: value });
                validateForm({ phoneNumber: value });
              }}
            />
          </View>
        </View>
        {errorMsg?.phoneCodeNumber && (
          <TextCaption danger sx={{ mt: '$1' }}>
            {errorMsg?.phoneCodeNumber}
          </TextCaption>
        )}
        {showView && <View sx={{ height: '$1', width: '100%' }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
          <UniButton sx={btn} label={t('card-account-management::change-atm-pin.button--update')} onPress={updateMobileNo} />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: '$1',
    marginTop: '$5'
  },
  bgView: {
    height: 74,
    backgroundColor: '$backgroundSecondary',
    borderRadius: '$radius2'
  },
  currentMobileView: {
    height: 42,
    margin: '$4'
  },
  textCurrentMobile: {
    mb: '$1',
    color: '$textSecondary'
  },
  mobileView: {
    flexDirection: 'row'
  },
  mobileCodeView: {
    width: '20%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '80%'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
